import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import * as XLSX from 'xlsx';

import { SelloutService, SelloutSearchParams } from '../sellout.service';
import { SelloutHeader } from '../sellout.model';
import { Page } from '../../models/page-wrapper.model';
import { Customer, CustomerPickerSummary } from '../../master-data/customers/customers.model';
import { CustomersService } from '../../master-data/customers/customers.service';
import { EntityDropdownPickerComponent } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.component';
import { EntityDropdownPickerAdapter } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';

@Component({
  selector: 'app-list-sellout',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    CalendarModule,
    DropdownModule,
    TagModule,
    ToolbarModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    ProgressSpinnerModule,
    ToastModule,
    EntityDropdownPickerComponent
  ],
  providers: [MessageService],
  templateUrl: './list-sellout.component.html',
  styleUrl: './list-sellout.component.css'
})
export class ListSelloutComponent implements OnInit, OnDestroy {
  @ViewChild('dt') dt!: Table;

  sellouts: SelloutHeader[] = [];
  totalRecords = 0;
  isLoading = false;
  isExporting = false;
  private selloutSub?: Subscription;

  filterForm: FormGroup;
  customers: Customer[] = [];
  statusOptions = ['Draft', 'Changed', 'Settled', 'Cancelled', 'Not Settled'];
  customerPickerAdapter!: EntityDropdownPickerAdapter<CustomerPickerSummary, Customer>;

  constructor(
    private selloutService: SelloutService,
    private router: Router,
    private route: ActivatedRoute,
    private customerService: CustomersService,
    private messageService: MessageService,
    private fb: FormBuilder
  ) {
    this.filterForm = this.fb.group({
      id: [''],
      settlementId: [''],
      customers: [null],
      batchNumber: [''],
      status: [null],
      channel: [''],
      city: [''],
      startDateFrom: [null],
      endDateTo: [null]
    });
  }

  ngOnInit(): void {
    this.customerPickerAdapter = this.customerService.createCustomerPickerAdapter();

    this.customerService.getAllCustomers({ page: 0, size: 15000 }).subscribe(page => {
      this.customers = page.content;
    });
  }

  loadSellouts(event: TableLazyLoadEvent = {}): void {
    this.isLoading = true;
    const filters = this.filterForm.value;

    if (filters.id) {
      this.selloutSub?.unsubscribe();
      this.selloutSub = this.selloutService.getSelloutHeaderById(Number(filters.id)).subscribe({
        next: (sellout) => {
          this.sellouts = sellout ? [sellout] : [];
          this.totalRecords = this.sellouts.length;
          this.isLoading = false;
        },
        error: (err) => {
          this.sellouts = [];
          this.totalRecords = 0;
          this.isLoading = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Sellout not found.' });
          console.error('Error loading sellout by id:', err);
        }
      });
      return;
    }

    const searchParams: SelloutSearchParams = {
      settlementId: filters.settlementId ? Number(filters.settlementId) : undefined,
      customerNumber: filters.customers?.customerNumber || undefined,
      batchNumber: filters.batchNumber || undefined,
      status: filters.status || undefined,
      channel: filters.channel || undefined,
      city: filters.city || undefined,
      startDateFrom: this.formatDate(filters.startDateFrom),
      endDateTo: this.formatDate(filters.endDateTo),
      page: (event.first ?? 0) / (event.rows ?? 25),
      size: event.rows ?? 25,
      sort: this.formatSort(event.sortField, event.sortOrder) || 'selloutId,desc'
    };

    Object.keys(searchParams).forEach(key =>
      (searchParams[key as keyof SelloutSearchParams] == null || searchParams[key as keyof SelloutSearchParams] === '') &&
      delete searchParams[key as keyof SelloutSearchParams]
    );

    this.selloutSub?.unsubscribe();
    this.selloutSub = this.selloutService.searchSelloutHeaders(searchParams).subscribe({
      next: (data: Page<SelloutHeader>) => {
        this.sellouts = data.content;
        this.totalRecords = data.totalElements;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load sellouts.' });
        console.error('Error loading sellouts:', err);
      }
    });
  }

  onSearch(): void {
    this.dt.reset();
  }

  onReset(): void {
    this.filterForm.reset();
    this.dt.reset();
  }

  viewSellout(selloutId: number): void {
    this.router.navigate(['.', { outlets: { canvasOutlet: ['sellout', 'view', selloutId] } }], { relativeTo: this.route.parent?.parent });
  }

  getStatusSeverity(sellout: SelloutHeader): string {
    switch (sellout.workflowStatus?.statusName) {
      case 'Settled':
      case 'Completed':
        return 'success';
      case 'Cancelled':
        return 'danger';
      case 'Changed':
        return 'warn';
      default:
        return 'info';
    }
  }

  getValidationStatusSeverity(status?: string): string {
    switch (status) {
      case 'VALIDATED':
        return 'success';
      case 'VARIANCE_FOUND':
        return 'warn';
      case 'VARIANCE_OVERRIDE':
        return 'contrast';
      case 'MAPPING_ISSUE':
        return 'danger';
      case 'NOT_MATCHED':
        return 'danger';
      default:
        return 'info';
    }
  }

  exportExcel(): void {
    this.isExporting = true;
    const dataToExport = this.sellouts.map(sellout => ({
      'SELLOUT ID': sellout.selloutId,
      'SETTLEMENT ID': sellout.settlementId,
      'CUSTOMER NUMBER': sellout.customer?.customerNumber,
      'CUSTOMER NAME': sellout.customer?.customerName,
      'CLAIM BRAND': sellout.business?.businessName,
      'CHANNEL': sellout.channel,
      'CITY': sellout.city,
      'BATCH NUMBER': sellout.batchNumber,
      'START DATE': sellout.selloutStartDate,
      'END DATE': sellout.selloutEndDate,
      'DETAIL COUNT': sellout.details?.length ?? 0,
      'UPLOADED BY': sellout.createdBy || '',
      'VALIDATED BY': sellout.overrideBy || sellout.createdBy || '',
      'VALIDATION STATUS': sellout.validationStatus,
      'WORKFLOW STATUS': sellout.workflowStatus?.statusName
    }));

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sellouts');
    XLSX.writeFile(wb, 'sellouts.xlsx');
    this.isExporting = false;
  }

  ngOnDestroy(): void {
    this.selloutSub?.unsubscribe();
  }

  private formatDate(date: Date | null): string | undefined {
    if (!date) {
      return undefined;
    }
    return new Date(date).toISOString().split('T')[0];
  }

  private formatSort(sortField: string | string[] | undefined | null, sortOrder: number | null | undefined): string | undefined {
    if (!sortField || Array.isArray(sortField) || !sortOrder) {
      return undefined;
    }

    return `${sortField},${sortOrder === 1 ? 'asc' : 'desc'}`;
  }
}
